import React, {
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef
} from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { Box, Icon, ThemeProvider, Text } from '@front10/base-elements';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import { Listbox } from '../../lab/Listbox';
import { useIsMobileView } from '@front10/utils';

const RedemptionUnitFilter = (props, ref) => {
  const {
    value,
    options = [
      { label: 'cmp-cash-label', value: 'CASH' },
      { label: 'cmp-loyalty-unit-label', value: 'MILES' }
    ],
    disabled,
    isMobile = false,
    onChange,
    filterButtonIconProps,
    menuContainer = {
      'cmp-styles': {
        width: '100%'
      }
    },
    itemContainer = {
      'cmp-styles': {
        height: '50px',
        'line-height': '50px'
      }
    },
    selectedItem = {
      'font-weight': 'bold',
      'padding-right': '10px'
    },
    item = {
      borderColor: 'borders',
      'font-weight': 'normal',
      'padding-right': '10px'
    },
    iconCheckProps = {}
  } = props;

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const title = translate('cmp-redemption-unit', 'Redemption unit');
  const valueTranslated = useMemo(() => {
    return options
      .filter((option) => option.value === value)
      .map((option) => {
        return translate(option.label);
      })
      .pop();
  }, [options, translate, value]);

  const redemptionUnitOptions = useMemo(() => {
    return (Array.isArray(options) ? options : []).map((option) => ({
      ...option,
      label: translate(option.label)
    }));
  }, [options, translate]);

  const [redemptionFilter, setRedemptionFilter] = useState(value);
  const flexibleRef = useRef();

  useEffect(() => {
    setRedemptionFilter(value);
  }, [value]);

  let mobileView = useIsMobileView();
  if (isMobile) {
    mobileView = true;
  }

  const listboxRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        listboxRef.current.focusToggleElement();
      }
    }),
    []
  );

  const desktopLayout = (
    <Listbox
      ref={listboxRef}
      label={title}
      options={redemptionUnitOptions}
      defaultValue={value}
      forcePlaceholder={disabled}
      focusWhenIsShow={false}
      disabled={disabled}
      onChange={(value) => onChange({ value })}
      filterButtonIconProps={filterButtonIconProps}
    />
  );

  const mobileLayout = (
    <FlexibleFilterContainer
      ref={flexibleRef}
      showSeparator
      saveOnClose={false}
      onBackButtonPress={() => setRedemptionFilter(value)}
      onSave={() => {
        props.onChange({ value: redemptionFilter });
      }}
      variant="layout.popover"
      label={title}
      title={title}
      displayFooter={true}
      toggleElement={
        <FilterButton
          iconSettings={filterButtonIconProps}
          value={value && !disabled ? `${valueTranslated}` : title}
          aria-label={`${title} ${value ? valueTranslated : ''}`}
          active={!disabled && Boolean(value)}
          disabled={disabled} // used to disabled the component
        />
      }
    >
      <Panels showSeparator={false}>
        <Box {...menuContainer}>
          {options.map((option) => (
            <Box {...itemContainer}>
              <Box
                onClick={() => setRedemptionFilter(option.value)}
                className="cmp-list-selector-item"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  variant: 'button.base',
                  padding: '15px 10px',
                  borderBottom: true,
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid',
                  borderBottomColor: item.borderColor
                }}
              >
                <Text
                  sx={{}}
                  variant="text.fontSize5"
                  style={redemptionFilter === option.value ? selectedItem : item}
                >
                  {translate(option.label)}
                </Text>
                {redemptionFilter === option.value && (
                  <Icon
                    className="fr-rotate"
                    icon="check"
                    size={20}
                    sx={{
                      stroke: 'primary',
                      verticalAlign: 'middle',
                      strokeWidth: '1px'
                    }}
                    {...iconCheckProps}
                  ></Icon>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Panels>
    </FlexibleFilterContainer>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        {!mobileView ? desktopLayout : mobileLayout}
      </ThemeProvider>
    </EmotionCache>
  );
};

export default forwardRef(RedemptionUnitFilter);
